import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { v4 as uuidv4 } from 'uuid';

export default function Home() {
  const navigate = useNavigate();
  const [nickname, setNickname] = useState('');
  const [gender, setGender] = useState<'male' | 'female' | ''>('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [validSession, setValidSession] = useState<any>(null);
  const [tokenError, setTokenError] = useState('');
  const [participantCount, setParticipantCount] = useState(0);
  const [isSessionFull, setIsSessionFull] = useState(false);

  useEffect(() => {
    const checkToken = async () => {
      // URL에서 토큰 확인
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('token');
      
      if (!token) {
        setTokenError('유효한 참여 링크가 필요합니다. 관리자에게 참여 링크를 요청해주세요.');
        setIsReady(true);
        return;
      }

      try {
        // 토큰으로 활성화된 세션 확인
        const { data: sessions, error: sessionError } = await supabase
          .from('sessions')
          .select('*')
          .eq('token', token)
          .eq('is_active', true);

        if (sessionError) {
          console.error('세션 조회 오류:', sessionError);
          setTokenError('참여 링크 확인 중 오류가 발생했습니다.');
          setIsReady(true);
          return;
        }

        if (!sessions || sessions.length === 0) {
          setTokenError('유효하지 않거나 만료된 참여 링크입니다.');
          setIsReady(true);
          return;
        }

        const session = sessions[0];
        
        // 현재 참가자 수 확인
        const { data: participants, error: participantsError } = await supabase
          .from('participants')
          .select('*')
          .eq('session_id', session.id);

        if (participantsError) {
          console.error('참가자 조회 오류:', participantsError);
          setTokenError('참가자 정보 확인 중 오류가 발생했습니다.');
          setIsReady(true);
          return;
        }

        const currentParticipantCount = participants?.length || 0;
        setParticipantCount(currentParticipantCount);

        // 이미 참여한 사용자인지 확인
        const deviceId = getDeviceId();
        const existingParticipant = participants?.find(p => p.device_id === deviceId);

        if (existingParticipant) {
          // 이미 참여한 경우 localStorage에 정보 저장 후 매칭 결과로 이동
          localStorage.setItem('participantId', existingParticipant.id);
          localStorage.setItem('sessionId', session.id);
          localStorage.setItem('participant', JSON.stringify({
            nickname: existingParticipant.nickname,
            gender: existingParticipant.gender,
            sessionId: session.id,
            deviceId: existingParticipant.device_id,
            timestamp: Date.now()
          }));
          navigate('/matching');
          return;
        }

        // 정원 초과 확인 (기존 참가자가 아닌 경우에만)
        if (currentParticipantCount >= session.expected_participants) {
          setIsSessionFull(true);
          setValidSession(session);
          setIsReady(true);
          return;
        }

        setValidSession(session);
        console.log('유효한 세션:', session);
      } catch (err) {
        console.error('토큰 검증 오류:', err);
        setTokenError('참여 링크 확인 중 오류가 발생했습니다.');
      } finally {
        setIsReady(true);
      }
    };

    checkToken();
  }, [navigate]);

  // 디바이스 ID 생성 또는 가져오기
  const getDeviceId = () => {
    let deviceId = localStorage.getItem('deviceId');
    if (!deviceId) {
      deviceId = uuidv4();
      localStorage.setItem('deviceId', deviceId);
    }
    return deviceId;
  };

  const handleStart = async () => {
    if (!validSession) {
      setError('유효한 세션이 없습니다.');
      return;
    }

    if (!nickname.trim()) {
      setError('닉네임을 입력해주세요');
      return;
    }
    if (!gender) {
      setError('성별을 선택해주세요');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const deviceId = getDeviceId();
      
      // 다시 한번 참가자 수 확인 (동시 접속 대비)
      const { data: participants } = await supabase
        .from('participants')
        .select('*')
        .eq('session_id', validSession.id);

      const existingParticipant = participants?.find(p => p.device_id === deviceId);

      if (existingParticipant) {
        // 이미 참여한 경우 localStorage에 정보 저장 후 매칭 결과로 이동
        localStorage.setItem('participantId', existingParticipant.id);
        localStorage.setItem('sessionId', validSession.id);
        localStorage.setItem('participant', JSON.stringify({
          nickname: existingParticipant.nickname,
          gender: existingParticipant.gender,
          sessionId: validSession.id,
          deviceId: existingParticipant.device_id,
          timestamp: Date.now()
        }));
        navigate('/matching');
        return;
      }

      // 정원 초과 재확인
      const currentCount = participants?.length || 0;
      if (currentCount >= validSession.expected_participants) {
        setIsSessionFull(true);
        setParticipantCount(currentCount);
        setError('참여 인원이 마감되었습니다. 다른 회차를 이용해주세요.');
        return;
      }

      // 참가자 정보를 localStorage에 임시 저장
      localStorage.setItem('participant', JSON.stringify({
        nickname: nickname.trim(),
        gender,
        sessionId: validSession.id,
        deviceId,
        timestamp: Date.now()
      }));

      // 자기소개서 작성 페이지로 이동
      navigate('/profile');
    } catch (err: any) {
      console.error('Error:', err);
      setError('오류가 발생했습니다. 페이지를 새로고침 후 다시 시도해주세요.');
    } finally {
      setLoading(false);
    }
  };

  if (!isReady) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 font-semibold">로딩 중...</p>
        </div>
      </div>
    );
  }

  // 토큰 오류가 있는 경우
  if (tokenError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-red-100 rounded-full mb-4">
              <i className="ri-error-warning-fill text-red-500 text-4xl"></i>
            </div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">접근 제한</h1>
            <p className="text-gray-600">{tokenError}</p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-6 text-center">
            <div className="mb-6">
              <i className="ri-shield-check-line text-purple-500 text-6xl mb-4"></i>
              <h3 className="text-lg font-bold text-gray-800 mb-2">참여 방법</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                소개팅 매칭에 참여하려면 관리자가 제공한 전용 참여 링크를 통해 접속해야 합니다.
              </p>
            </div>
            
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs text-gray-500">
                참여 링크가 없으시다면 관리자에게 문의해주세요.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 정원 초과인 경우
  if (isSessionFull && validSession) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-orange-100 rounded-full mb-4">
              <i className="ri-user-forbid-fill text-orange-500 text-4xl"></i>
            </div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">참여 인원 마감</h1>
            <p className="text-gray-600">죄송합니다. 참여 인원이 마감되었습니다.</p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-6">
            <div className="mb-6">
              <div className="bg-purple-100 text-purple-700 px-4 py-2 rounded-full text-sm font-semibold inline-block mb-4">
                {validSession.name}
              </div>
              
              <div className="bg-orange-50 rounded-xl p-4 mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-orange-700 font-semibold">참여 현황</span>
                  <span className="text-2xl font-bold text-orange-600">
                    {participantCount}/{validSession.expected_participants}명
                  </span>
                </div>
                <div className="w-full bg-orange-200 rounded-full h-3">
                  <div 
                    className="bg-orange-500 h-3 rounded-full transition-all"
                    style={{ width: '100%' }}
                  ></div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-sm text-gray-700 leading-relaxed">
                  <i className="ri-information-line text-gray-500 mr-2"></i>
                  현재 회차는 정원({validSession.expected_participants}명)이 모두 마감되었습니다.
                  다른 회차의 참여 링크를 관리자에게 요청해주세요.
                </p>
              </div>
            </div>

            <div className="text-center">
              <p className="text-xs text-gray-500">
                문의사항은 관리자에게 연락해주세요
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* 헤더 */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-pink-500 to-purple-600 rounded-full mb-4">
            <i className="ri-heart-fill text-white text-4xl"></i>
          </div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">소개팅 매칭</h1>
          <p className="text-gray-600">설문을 통해 나와 잘 맞는 사람을 찾아보세요</p>
          {validSession && (
            <div className="mt-3">
              <div className="inline-block bg-purple-100 text-purple-700 px-4 py-2 rounded-full text-sm font-semibold mb-2">
                {validSession.name}
              </div>
              <div className="text-sm text-gray-600">
                <span className="font-semibold text-purple-600">{participantCount}</span>
                <span className="text-gray-500"> / {validSession.expected_participants}명 참여 중</span>
              </div>
            </div>
          )}
        </div>

        {/* 입력 폼 */}
        <div className="bg-white rounded-2xl shadow-xl p-6 space-y-6">
          {/* 닉네임 입력 */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              닉네임
            </label>
            <input
              type="text"
              value={nickname}
              onChange={(e) => {
                setNickname(e.target.value);
                setError('');
              }}
              placeholder="사용할 닉네임을 입력하세요"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:outline-none transition-colors text-base"
              maxLength={20}
              disabled={loading}
            />
          </div>

          {/* 성별 선택 */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              성별
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => {
                  setGender('male');
                  setError('');
                }}
                disabled={loading}
                className={`py-4 px-6 rounded-xl border-2 transition-all whitespace-nowrap cursor-pointer ${
                  gender === 'male'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 hover:border-blue-300 text-gray-700'
                } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <div className="flex flex-col items-center gap-2">
                  <i className="ri-men-fill text-2xl"></i>
                  <span className="font-semibold">남성</span>
                </div>
              </button>
              <button
                type="button"
                onClick={() => {
                  setGender('female');
                  setError('');
                }}
                disabled={loading}
                className={`py-4 px-6 rounded-xl border-2 transition-all whitespace-nowrap cursor-pointer ${
                  gender === 'female'
                    ? 'border-pink-500 bg-pink-50 text-pink-700'
                    : 'border-gray-200 hover:border-pink-300 text-gray-700'
                } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <div className="flex flex-col items-center gap-2">
                  <i className="ri-women-fill text-2xl"></i>
                  <span className="font-semibold">여성</span>
                </div>
              </button>
            </div>
          </div>

          {/* 에러 메시지 */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
              {error}
            </div>
          )}

          {/* 시작 버튼 */}
          <button
            onClick={handleStart}
            disabled={loading}
            className="w-full bg-gradient-to-r from-pink-500 to-purple-600 text-white py-4 rounded-xl font-bold text-lg hover:from-pink-600 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? '처리중...' : '자기소개서 작성하기'}
          </button>

          {/* 안내 문구 */}
          <div className="text-center text-sm text-gray-500 pt-2">
            <p>자기소개서 작성 → 설문조사 → 매칭 결과</p>
            <p className="mt-1">약 10분 정도 소요됩니다</p>
          </div>
        </div>

        {/* 하단 정보 */}
        <div className="mt-6 text-center text-xs text-gray-500">
          <p>모든 정보는 매칭 목적으로만 사용됩니다</p>
        </div>
      </div>
    </div>
  );
}
